import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { ExpenseCell } from "@/components/expenses/table/ExpenseCell";
import { HeaderFilterCell } from "@/components/expenses/table/HeaderFilterCell";
import { RowActions } from "@/components/expenses/table/RowActions";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fmtUSD } from "@/lib/api";

const STATUS_GROUPS = [
  { key: "needs_review", label: "Needs review", color: "#7FAEF9" },
  { key: "waiting_on_employee", label: "Waiting on employee", color: "#FBBF24" },
  { key: "draft", label: "Drafts", color: "#8a8a8a" },
  { key: "approved", label: "Approved", color: "#4ADE80" },
];

// Flattens items into a render list of group separators + rows.
const buildRenderList = (items, { grouped, assignedView }) => {
  const list = [];
  if (assignedView) {
    const byAssignee = new Map();
    items.forEach((e) => {
      const name = e.assigned_to?.name || "Unassigned";
      if (!byAssignee.has(name)) byAssignee.set(name, []);
      byAssignee.get(name).push(e);
    });
    const names = [...byAssignee.keys()].sort(
      (a, b) => (a === "Unassigned") - (b === "Unassigned") || a.localeCompare(b));
    names.forEach((name) => {
      const group = byAssignee.get(name);
      list.push({
        type: "sep", count: group.length,
        g: {
          key: `assignee-${name}`,
          label: name === "Unassigned" ? "Unassigned" : `Assigned to ${name}`,
          color: name === "Unassigned" ? "#8a8a8a" : "#7FAEF9",
        },
      });
      group.forEach((e) => list.push({ type: "row", e }));
    });
    return list;
  }
  if (grouped) {
    const seen = new Set();
    for (const g of STATUS_GROUPS) {
      const group = items.filter((e) => e.status === g.key);
      if (group.length) {
        list.push({ type: "sep", g, count: group.length });
        group.forEach((e) => { list.push({ type: "row", e }); seen.add(e.id); });
      }
    }
    items.forEach((e) => { if (!seen.has(e.id)) list.push({ type: "row", e }); });
    return list;
  }
  items.forEach((e) => list.push({ type: "row", e }));
  return list;
};

export const ExpensesTable = ({
  cols, data, loading, sort, dir, onSort,
  selected, allChecked, toggleAll, toggleOne,
  onOpen, onRowAction, onReceipt, page, totalPages, setPage,
  grouped = false,
  filters, onFilterChange, options,
  assignedView = false, headerActions,
}) => {
  const span = cols.length + 2;
  const renderList = buildRenderList(data.items, { grouped, assignedView });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border border-[#222222] bg-[#111111] card-shadow">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[1100px] text-left" data-testid="expenses-table">
          <thead className="sticky top-0 z-10 bg-[#141414]">
            <tr>
              <th className="w-10 px-3 py-2.5">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="expenses-select-all"
                  className="border-[#333] data-[state=checked]:bg-[#4F8EF7] data-[state=checked]:border-[#4F8EF7]" />
              </th>
              {cols.map((c) => (
                <HeaderFilterCell key={c.key} c={c} sort={sort} dir={dir} onSort={onSort}
                  filters={filters} onFilterChange={onFilterChange} options={options} />
              ))}
              <th className="sticky right-0 z-[1] w-24 bg-[#141414] px-2 py-2 shadow-[-10px_0_14px_-10px_rgba(0,0,0,0.7)]">
                <div className="flex items-center justify-end gap-0.5">{headerActions}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(8)].map((_, i) => (
                <tr key={i} className="border-b border-[#181818]">
                  <td className="px-3 py-3" colSpan={span}><Skeleton className="h-5 w-full bg-[#1a1a1a]" /></td>
                </tr>
              ))
            ) : renderList.length === 0 ? (
              <tr>
                <td colSpan={span} className="px-6 py-14 text-center">
                  <div className="text-[13px] text-[#A3A3A3]">Nothing here — Aria has this queue fully handled.</div>
                </td>
              </tr>
            ) : (
              renderList.map((item, i) => {
                if (item.type === "sep") {
                  return (
                    <tr key={`sep-${item.g.key}`} className="bg-[#0d0d0d]" data-testid={`expenses-group-${item.g.key}`}>
                      <td colSpan={span} className="px-4 py-1.5">
                        <span className="flex items-center gap-2 text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: item.g.color }}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: item.g.color }} />
                          {item.g.label}
                          <span className="font-normal normal-case tracking-normal text-[#555]">· {item.count}</span>
                        </span>
                      </td>
                    </tr>
                  );
                }
                const e = item.e;
                return (
                  <tr key={e.id} onClick={() => onOpen(e.id)} data-testid={`expenses-table-row-${e.id}`}
                    data-aria-key={`expense:${e.id}`} data-aria-label={`${e.merchant} ${fmtUSD(e.amount)} — ${e.employee_name || "Unassigned"}`}
                    style={{ animationDelay: `${Math.min(i, 14) * 22}ms` }}
                    className="anim-fade-up group cursor-pointer border-b border-[#181818] transition-colors hover:bg-[#161616]">
                    <td className="px-3 py-2.5" onClick={(ev) => ev.stopPropagation()}>
                      <Checkbox checked={selected.has(e.id)} onCheckedChange={() => toggleOne(e.id)}
                        data-testid={`expenses-row-checkbox-${e.id}`}
                        className="border-[#333] data-[state=checked]:bg-[#4F8EF7] data-[state=checked]:border-[#4F8EF7]" />
                    </td>
                    {cols.map((c) => (
                      <td key={c.key} className={`px-3 py-2.5 ${c.right ? "text-right" : ""}`}>
                        <ExpenseCell col={c.key} e={e} onReceipt={onReceipt} />
                      </td>
                    ))}
                    <td className="sticky right-0 z-[1] bg-[#111111] px-2 py-2.5 shadow-[-10px_0_14px_-10px_rgba(0,0,0,0.7)] transition-colors group-hover:bg-[#161616]" onClick={(ev) => ev.stopPropagation()}>
                      <RowActions e={e} onOpen={onOpen} onRowAction={onRowAction} />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-[#1e1e1e] px-4 py-2.5">
        <span className="text-[11.5px] text-[#6b6b6b]">
          Page {page} of {totalPages} · <span className="tabular-nums">{data.total}</span> results
        </span>
        <div className="flex gap-1">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="expenses-prev-page"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#222] text-[#A3A3A3] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30">
            <ChevronLeft size={13} />
          </button>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="expenses-next-page"
            className="flex h-7 w-7 items-center justify-center rounded-lg border border-[#222] text-[#A3A3A3] hover:bg-[#1a1a1a] hover:text-white disabled:opacity-30">
            <ChevronRight size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};
